import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { PartialBlock } from '@blocknote/core';
import { toast } from 'sonner';
import {
  Plus,
  Search,
  MoreVertical,
  Trash2,
  Archive,
  ArchiveRestore,
  ArrowLeft,
  RefreshCw,
  AlertCircle,
  Loader2,
  Check,
  NotebookPen,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Skeleton } from '@/components/ui/skeleton';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { NoteEditor } from '@/components/notes/NoteEditor';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/i18n';
import { useIsMobile } from '@/hooks/use-mobile';
import { supabase } from '@/lib/supabase';
import {
  CollaborativeNote,
  NOTE_TITLE_MAX,
  contentByteSize,
  createNote,
  deleteNote,
  getNote,
  listNotes,
  NOTE_CONTENT_MAX_BYTES,
  renameNote,
  setArchived,
  updateNoteContent,
} from '@/services/notesService';

type SaveState = 'idle' | 'saving' | 'saved' | 'error';
const AUTOSAVE_DEBOUNCE_MS = 1000;

export default function Notes() {
  const { user } = useAuth();
  const { t, lang } = useLanguage();
  const isMobile = useIsMobile();

  const [notes, setNotes] = useState<CollaborativeNote[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [search, setSearch] = useState('');
  const [showArchived, setShowArchived] = useState(false);

  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [activeContent, setActiveContent] = useState<unknown>(undefined);
  const [editorKey, setEditorKey] = useState(0);
  const [titleDraft, setTitleDraft] = useState('');

  const [saveState, setSaveState] = useState<SaveState>('idle');
  const [dirty, setDirty] = useState(false);
  const [conflict, setConflict] = useState<CollaborativeNote | null>(null);

  const [creating, setCreating] = useState(false);
  const [pendingSelectId, setPendingSelectId] = useState<string | null | undefined>(undefined);
  const [deleteTarget, setDeleteTarget] = useState<CollaborativeNote | null>(null);

  // Refs for autosave + stale-response protection.
  const selectedIdRef = useRef<string | null>(null);
  const editVersionRef = useRef(0);
  const savedContentRef = useRef<string>('');
  const lastKnownUpdatedAtRef = useRef<string | null>(null);
  const dirtyRef = useRef(false);
  const saveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    selectedIdRef.current = selectedId;
  }, [selectedId]);

  useEffect(() => {
    dirtyRef.current = dirty;
  }, [dirty]);

  const selectedNote = useMemo(
    () => notes.find((n) => n.id === selectedId) ?? null,
    [notes, selectedId],
  );

  const filteredNotes = useMemo(() => {
    const q = search.trim().toLowerCase();
    return notes
      .filter((n) => (showArchived ? true : !n.archived))
      .filter((n) => (q ? n.title.toLowerCase().includes(q) : true));
  }, [notes, search, showArchived]);

  const loadNotes = useCallback(async () => {
    setLoading(true);
    setLoadError(false);
    try {
      const rows = await listNotes(true);
      setNotes(rows);
    } catch {
      setLoadError(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadNotes();
  }, [loadNotes]);

  // Warn on browser unload while there are unsaved edits.
  useEffect(() => {
    const handler = (e: BeforeUnloadEvent) => {
      if (dirtyRef.current) {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, []);

  const upsertNoteInList = useCallback((row: CollaborativeNote) => {
    setNotes((prev) => {
      const exists = prev.some((n) => n.id === row.id);
      const next = exists ? prev.map((n) => (n.id === row.id ? row : n)) : [row, ...prev];
      return next.sort(
        (a, b) => new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime(),
      );
    });
  }, []);

  const openNote = useCallback(
    async (id: string) => {
      if (saveTimerRef.current) {
        clearTimeout(saveTimerRef.current);
        saveTimerRef.current = null;
      }
      setConflict(null);
      setSaveState('idle');
      setDirty(false);
      dirtyRef.current = false;
      setSelectedId(id);
      selectedIdRef.current = id;
      editVersionRef.current += 1;

      try {
        const note = await getNote(id);
        if (!note) {
          toast.error(t('notesLoadError'));
          setSelectedId(null);
          return;
        }
        if (selectedIdRef.current !== id) return;
        setActiveContent(note.content);
        savedContentRef.current = JSON.stringify(note.content ?? []);
        lastKnownUpdatedAtRef.current = note.updated_at;
        setTitleDraft(note.title);
        setEditorKey((k) => k + 1);
        upsertNoteInList(note);
      } catch {
        toast.error(t('notesLoadError'));
      }
    },
    [t, upsertNoteInList],
  );

  const requestSelect = useCallback(
    (id: string) => {
      if (id === selectedId) return;
      if (dirtyRef.current) {
        setPendingSelectId(id);
        return;
      }
      openNote(id);
    },
    [openNote, selectedId],
  );

  const persist = useCallback(
    async (noteId: string, content: PartialBlock[], version: number) => {
      setSaveState('saving');
      try {
        const updated = await updateNoteContent(noteId, content);
        // Stale-response guard: only settle "saved" if no newer edit happened and we
        // are still on the same note. A newer edit keeps the note dirty for the next save.
        if (noteId === selectedIdRef.current && version === editVersionRef.current) {
          savedContentRef.current = JSON.stringify(content);
          lastKnownUpdatedAtRef.current = updated.updated_at;
          setDirty(false);
          dirtyRef.current = false;
          setSaveState('saved');
        }
        upsertNoteInList(updated);
      } catch {
        if (noteId === selectedIdRef.current) {
          setSaveState('error');
        }
      }
    },
    [upsertNoteInList],
  );

  const handleEditorChange = useCallback(
    (content: PartialBlock[]) => {
      if (!selectedIdRef.current) return;
      const serialized = JSON.stringify(content);
      // Ignore spurious change events that don't actually modify content.
      if (serialized === savedContentRef.current) return;

      if (contentByteSize(content) > NOTE_CONTENT_MAX_BYTES) {
        setSaveState('error');
        toast.error(t('notesContentTooLarge'));
        return;
      }

      const noteId = selectedIdRef.current;
      editVersionRef.current += 1;
      const version = editVersionRef.current;
      setDirty(true);
      dirtyRef.current = true;
      setSaveState('saving');

      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
      saveTimerRef.current = setTimeout(() => {
        persist(noteId, content, version);
      }, AUTOSAVE_DEBOUNCE_MS);
    },
    [persist, t],
  );

  const handleCreate = useCallback(async () => {
    if (creating) return;
    setCreating(true);
    try {
      const note = await createNote(t('notesUntitled'));
      upsertNoteInList(note);
      await openNote(note.id);
    } catch {
      toast.error(t('notesCreateError'));
    } finally {
      setCreating(false);
    }
  }, [creating, openNote, t, upsertNoteInList]);

  const commitTitle = useCallback(async () => {
    if (!selectedId) return;
    const next = titleDraft.trim();
    if (!next) {
      toast.error(t('notesTitleRequired'));
      setTitleDraft(selectedNote?.title ?? '');
      return;
    }
    if (next === selectedNote?.title) return;
    try {
      const updated = await renameNote(selectedId, next);
      lastKnownUpdatedAtRef.current = updated.updated_at;
      upsertNoteInList(updated);
    } catch {
      toast.error(t('notesRenameError'));
      setTitleDraft(selectedNote?.title ?? '');
    }
  }, [selectedId, selectedNote, t, titleDraft, upsertNoteInList]);

  const handleArchiveToggle = useCallback(
    async (note: CollaborativeNote) => {
      try {
        const updated = await setArchived(note.id, !note.archived);
        upsertNoteInList(updated);
        if (note.id === selectedId) lastKnownUpdatedAtRef.current = updated.updated_at;
        toast.success(updated.archived ? t('notesArchived') : t('notesUnarchived'));
      } catch {
        toast.error(t('notesActionError'));
      }
    },
    [selectedId, t, upsertNoteInList],
  );

  const handleDelete = useCallback(async () => {
    if (!deleteTarget) return;
    const target = deleteTarget;
    setDeleteTarget(null);
    try {
      await deleteNote(target.id);
      setNotes((prev) => prev.filter((n) => n.id !== target.id));
      if (target.id === selectedId) {
        setSelectedId(null);
        setDirty(false);
        dirtyRef.current = false;
      }
      toast.success(t('notesDeleted'));
    } catch {
      toast.error(t('notesActionError'));
    }
  }, [deleteTarget, selectedId, t]);

  const reloadLatest = useCallback(async () => {
    if (!selectedId) return;
    setConflict(null);
    await openNote(selectedId);
  }, [openNote, selectedId]);

  // Realtime: keep the list and open note in sync across admin sessions.
  useEffect(() => {
    const channel = supabase
      .channel('collaborative_notes_changes')
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'collaborative_notes' },
        (payload) => {
          const eventType = payload.eventType;
          if (eventType === 'DELETE') {
            const oldId = (payload.old as { id?: string })?.id;
            if (!oldId) return;
            setNotes((prev) => prev.filter((n) => n.id !== oldId));
            if (oldId === selectedIdRef.current) {
              setSelectedId(null);
              setDirty(false);
              dirtyRef.current = false;
              setConflict(null);
              toast.info(t('notesDeletedElsewhere'));
            }
            return;
          }

          const row = payload.new as CollaborativeNote;
          if (!row?.id) return;
          upsertNoteInList(row);

          if (row.id === selectedIdRef.current && eventType === 'UPDATE') {
            // Ignore the echo of our own save.
            if (row.updated_at === lastKnownUpdatedAtRef.current) return;

            if (dirtyRef.current) {
              setConflict(row);
            } else {
              lastKnownUpdatedAtRef.current = row.updated_at;
              setActiveContent(row.content);
              savedContentRef.current = JSON.stringify(row.content ?? []);
              setTitleDraft(row.title);
              setEditorKey((k) => k + 1);
            }
          }
        },
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [t, upsertNoteInList]);

  useEffect(() => {
    return () => {
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    };
  }, []);

  const formatUpdatedAt = (iso: string) =>
    new Date(iso).toLocaleString(lang === 'zh' ? 'zh-CN' : 'en-US', {
      dateStyle: 'medium',
      timeStyle: 'short',
    });

  const updatedByLabel = (note: CollaborativeNote) => {
    if (!note.updated_by) return t('notesUnknownUser');
    if (note.updated_by === user?.id) return t('notesYou');
    return note.updated_by.slice(0, 8);
  };

  const saveIndicator = () => {
    if (saveState === 'saving') {
      return (
        <span className="flex items-center gap-1.5 text-muted-foreground">
          <Loader2 className="h-3.5 w-3.5 animate-spin" /> {t('notesSaving')}
        </span>
      );
    }
    if (saveState === 'saved') {
      return (
        <span className="flex items-center gap-1.5 text-emerald-500">
          <Check className="h-3.5 w-3.5" /> {t('notesSaved')}
        </span>
      );
    }
    if (saveState === 'error') {
      return (
        <span className="flex items-center gap-1.5 text-destructive">
          <AlertCircle className="h-3.5 w-3.5" /> {t('notesSaveFailed')}
        </span>
      );
    }
    return null;
  };

  const showListPanel = !isMobile || !selectedId;
  const showEditorPanel = !isMobile || !!selectedId;

  const listPanel = (
    <div className="flex h-full flex-col border-r bg-card/40">
      <div className="p-3 space-y-3 border-b">
        <div className="flex items-center gap-2">
          <h1 className="flex items-center gap-2 text-lg font-semibold">
            <NotebookPen className="h-5 w-5" /> {t('navNotes')}
          </h1>
          <Button size="sm" className="ml-auto" onClick={handleCreate} disabled={creating}>
            {creating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
            <span className="ml-1 hidden sm:inline">{t('notesNew')}</span>
          </Button>
        </div>
        <div className="relative">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t('notesSearch')}
            className="pl-8"
          />
        </div>
        <Button
          variant="ghost"
          size="sm"
          className="w-full justify-start text-muted-foreground"
          onClick={() => setShowArchived((v) => !v)}
        >
          <Archive className="mr-2 h-4 w-4" />
          {showArchived ? t('notesHideArchived') : t('notesShowArchived')}
        </Button>
      </div>

      <ScrollArea className="flex-1">
        <div className="p-2 space-y-1">
          {loading ? (
            Array.from({ length: 5 }).map((_, i) => (
              <Skeleton key={i} className="h-14 w-full rounded-md" />
            ))
          ) : loadError ? (
            <div className="p-6 text-center text-sm text-muted-foreground space-y-3">
              <AlertCircle className="mx-auto h-6 w-6 text-destructive" />
              <p>{t('notesLoadError')}</p>
              <Button variant="outline" size="sm" onClick={loadNotes}>
                <RefreshCw className="mr-2 h-4 w-4" /> {t('notesRetry')}
              </Button>
            </div>
          ) : filteredNotes.length === 0 ? (
            <div className="p-6 text-center text-sm text-muted-foreground space-y-3">
              <NotebookPen className="mx-auto h-6 w-6 opacity-60" />
              <p>{search ? t('notesNoResults') : t('notesEmpty')}</p>
              {!search && (
                <Button variant="outline" size="sm" onClick={handleCreate} disabled={creating}>
                  <Plus className="mr-2 h-4 w-4" /> {t('notesCreateFirst')}
                </Button>
              )}
            </div>
          ) : (
            filteredNotes.map((note) => (
              <div
                key={note.id}
                className={`group flex items-center gap-2 rounded-md px-3 py-2 cursor-pointer transition-colors ${
                  note.id === selectedId ? 'bg-primary/10' : 'hover:bg-secondary/60'
                }`}
                onClick={() => requestSelect(note.id)}
              >
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className="truncate text-sm font-medium">{note.title}</span>
                    {note.archived && (
                      <Archive className="h-3 w-3 shrink-0 text-muted-foreground" />
                    )}
                  </div>
                  <span className="block truncate text-xs text-muted-foreground">
                    {formatUpdatedAt(note.updated_at)}
                  </span>
                </div>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7 opacity-0 group-hover:opacity-100 data-[state=open]:opacity-100"
                    >
                      <MoreVertical className="h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
                    <DropdownMenuItem onClick={() => handleArchiveToggle(note)}>
                      {note.archived ? (
                        <>
                          <ArchiveRestore className="mr-2 h-4 w-4" /> {t('notesUnarchive')}
                        </>
                      ) : (
                        <>
                          <Archive className="mr-2 h-4 w-4" /> {t('notesArchive')}
                        </>
                      )}
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      className="text-destructive focus:text-destructive"
                      onClick={() => setDeleteTarget(note)}
                    >
                      <Trash2 className="mr-2 h-4 w-4" /> {t('delete')}
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            ))
          )}
        </div>
      </ScrollArea>
    </div>
  );

  const editorPanel = (
    <div className="flex h-full flex-col">
      {!selectedId ? (
        <div className="flex flex-1 flex-col items-center justify-center gap-3 text-muted-foreground">
          <NotebookPen className="h-10 w-10 opacity-50" />
          <p className="text-sm">{t('notesSelectPrompt')}</p>
        </div>
      ) : (
        <>
          <div className="border-b p-3 space-y-2">
            <div className="flex items-center gap-2">
              {isMobile && (
                <Button
                  variant="ghost"
                  size="icon"
                  className="shrink-0"
                  onClick={() => {
                    if (dirtyRef.current) {
                      setPendingSelectId(null);
                    } else {
                      setSelectedId(null);
                    }
                  }}
                >
                  <ArrowLeft className="h-5 w-5" />
                </Button>
              )}
              <Input
                value={titleDraft}
                maxLength={NOTE_TITLE_MAX}
                onChange={(e) => setTitleDraft(e.target.value)}
                onBlur={commitTitle}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    (e.target as HTMLInputElement).blur();
                  }
                }}
                placeholder={t('notesTitlePlaceholder')}
                className="border-none bg-transparent text-lg font-semibold shadow-none focus-visible:ring-0 px-1"
              />
            </div>
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1 px-1 text-xs text-muted-foreground">
              {saveIndicator()}
              {selectedNote && (
                <span>
                  {t('notesLastUpdated', {
                    time: formatUpdatedAt(selectedNote.updated_at),
                    user: updatedByLabel(selectedNote),
                  })}
                </span>
              )}
            </div>
          </div>

          {conflict && (
            <div className="flex flex-wrap items-center gap-3 border-b bg-amber-500/10 px-4 py-2 text-sm text-amber-600 dark:text-amber-400">
              <AlertCircle className="h-4 w-4 shrink-0" />
              <span className="flex-1">{t('notesUpdatedElsewhere')}</span>
              <Button size="sm" variant="outline" onClick={reloadLatest}>
                <RefreshCw className="mr-2 h-4 w-4" /> {t('notesReloadLatest')}
              </Button>
            </div>
          )}

          <ScrollArea className="flex-1">
            <div className="mx-auto max-w-3xl px-2 py-4 sm:px-6">
              <NoteEditor
                key={`${selectedId}:${editorKey}`}
                initialContent={activeContent}
                onChange={handleEditorChange}
              />
            </div>
          </ScrollArea>
        </>
      )}
    </div>
  );

  return (
    <div className="-my-6 -mx-6 h-[calc(100vh-4rem)]">
      <div className="grid h-full grid-cols-1 md:grid-cols-[20rem_1fr]">
        {showListPanel && <div className="h-full overflow-hidden">{listPanel}</div>}
        {showEditorPanel && <div className="h-full overflow-hidden">{editorPanel}</div>}
      </div>

      {/* Unsaved-changes switch confirmation */}
      <AlertDialog
        open={pendingSelectId !== undefined}
        onOpenChange={(open) => {
          if (!open) setPendingSelectId(undefined);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('notesUnsavedTitle')}</AlertDialogTitle>
            <AlertDialogDescription>{t('notesUnsavedBody')}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                const target = pendingSelectId;
                setPendingSelectId(undefined);
                setDirty(false);
                dirtyRef.current = false;
                if (saveTimerRef.current) {
                  clearTimeout(saveTimerRef.current);
                  saveTimerRef.current = null;
                }
                if (target) {
                  openNote(target);
                } else {
                  setSelectedId(null);
                }
              }}
            >
              {t('notesDiscardSwitch')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete confirmation */}
      <AlertDialog open={!!deleteTarget} onOpenChange={(open) => !open && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('areYouSure')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('notesDeleteConfirm', { name: deleteTarget?.title ?? '' })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('cancel')}</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={handleDelete}
            >
              {t('delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
